import { DEFAULT_TZ } from '@tahi/core';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSession } from '../app/auth/session';
import { FailedScreen, LoadingScreen, OtherTabScreen } from '../app/shell/StatusScreens';
import { computeUsable } from './access';
import { ApiError } from './api';
import { ensureTodaySeeded } from './commands/tiles';
import { DataContext, type DataContextValue } from './context';
import { DbContext } from './dbContext';
import { getDeviceId } from './deviceId';
import type { SqlDriver, StorageMode } from './driver';
import type { LocalDb } from './localDb';
import { loadMe, type MeResponse } from './me';
import { openLocalDb } from './openLocalDb';
import { listModuleStates } from './repos/moduleStates';
import { requestPersistentStorage } from './storage';
import { DB_MIGRATIONS } from './migrations';
import { createSyncClient, type SyncClient } from './syncClient';
import { acquireTabLock } from './tabLock';

type Phase =
  | { kind: 'opening' }
  | { kind: 'other_tab' }
  | { kind: 'failed'; message: string }
  | { kind: 'ready'; db: LocalDb; mode: StorageMode; persisted: boolean | null; sync: SyncClient };

/** What the provider must release: the sync loop, the worker connection, and the tab lock. */
interface Held {
  sync: SyncClient | null;
  driver: SqlDriver | null;
  release: (() => void) | null;
}

export function DataProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const session = useSession();
  const [phase, setPhase] = useState<Phase>({ kind: 'opening' });
  const [me, setMe] = useState<MeResponse | null>(null);
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ, []);
  const held = useRef<Held>({ sync: null, driver: null, release: null });

  useEffect(() => {
    let cancelled = false;
    let onVisible: (() => void) | null = null;
    const h = held.current;

    (async () => {
      const release = await acquireTabLock('tahi-db');
      if (cancelled) return release?.();
      if (!release) return setPhase({ kind: 'other_tab' });
      h.release = release;

      const opened = await openLocalDb(userId);
      if (cancelled) {
        await opened.driver.close();
        return;
      }
      h.driver = opened.driver;
      const db = opened.db;
      const persisted = opened.driver.mode === 'opfs' ? await requestPersistentStorage() : null;

      let meNow: MeResponse | null = null;
      try {
        meNow = await loadMe(db);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return void session.refresh();
        throw err;
      }
      if (cancelled) return;
      setMe(meNow);

      const deviceId = await getDeviceId(db);
      if (cancelled) return;
      const sync = createSyncClient({
        db,
        userId,
        deviceId,
        onSignedOut: () => void session.refresh(),
        schema: DB_MIGRATIONS[DB_MIGRATIONS.length - 1]?.name,
      });
      h.sync = sync;
      setPhase({ kind: 'ready', db, mode: opened.driver.mode, persisted, sync });
      sync.start();

      onVisible = () => {
        if (document.visibilityState === 'visible')
          void loadMe(db)
            .then((m) => !cancelled && setMe(m))
            .catch(() => {});
      };
      document.addEventListener('visibilitychange', onVisible);

      await sync.syncNow();
      if (cancelled) return;
      const usable = computeUsable(meNow, await listModuleStates(db));
      await ensureTodaySeeded({ db, userId, tz, sync, usable: (id) => usable[id] });
    })().catch((err: unknown) => {
      if (!cancelled)
        setPhase({ kind: 'failed', message: err instanceof Error ? err.message : String(err) });
    });

    return () => {
      cancelled = true;
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      h.sync?.stop();
      const { driver, release } = h;
      h.sync = null;
      h.driver = null;
      h.release = null;
      if (driver)
        void driver
          .close()
          .catch(() => {})
          .finally(() => release?.());
      else release?.();
    };
    // session.refresh is stable (useCallback); re-run only for a different account
  }, [userId, tz]);

  const refreshMe = useCallback(async () => {
    if (phase.kind === 'ready') setMe(await loadMe(phase.db));
  }, [phase]);

  /** Sign-out (conventions §15): stop syncing, delete this account's database file, free the tab lock. */
  const wipeLocalData = useCallback(async () => {
    const h = held.current;
    h.sync?.stop();
    const { driver, release } = h;
    h.sync = null;
    h.driver = null;
    h.release = null;
    try {
      await driver?.wipe();
    } finally {
      release?.();
    }
  }, []);

  const value = useMemo<DataContextValue | null>(
    () =>
      phase.kind === 'ready'
        ? {
            db: phase.db,
            userId,
            tz,
            mode: phase.mode,
            persisted: phase.persisted,
            sync: phase.sync,
            me,
            refreshMe,
            wipeLocalData,
          }
        : null,
    [phase, userId, tz, me, refreshMe, wipeLocalData],
  );

  if (phase.kind === 'other_tab') return <OtherTabScreen />;
  if (phase.kind === 'failed') return <FailedScreen message={phase.message} />;
  if (!value) return <LoadingScreen />;
  return (
    <DbContext.Provider value={value.db}>
      <DataContext.Provider value={value}>{children}</DataContext.Provider>
    </DbContext.Provider>
  );
}
